'use strict'

import SwatchAPI from '../api/SwatchAPI'
import ColorsMath from '../cxcolors/ColorsMath'
import Utils from '../Utils.js'

/**
 * SwatchDataCache
 * Workflow:
 * - Load all swatches + texture swatches in parallel.
 * - Store normalized lookups for closest-colors and global search.
 */
class SwatchDataCache {
    constructor() {
        this.isLoaded = false
        this.isLoading = false
        this.loadPromise = null

        this.visibleSwatches = new Map()
        this.invisibleSwatches = new Map()
        this.allSwatches = new Map()
        this.visibleSwatchIds = new Set()
        this.invisibleSwatchIds = new Set()

        this.swatchColorsBySwatch = new Map()
        this.swatchColorById = new Map()
        this.computeColorsBySwatch = new Map()
        this.computeColorList = []

        this.textureSwatches = new Map()
        this.texturesById = new Map()
        // Track in-flight requests to prevent duplicate fetches.
        this.pendingSwatchRequests = new Map()
        this.pendingTextureRequests = new Map()

        this.colorSearchIndex = []
        this.textureSearchIndex = []
    }

    /**
     * Singleton accessor to avoid duplicate loads.
     * @returns {SwatchDataCache}
     */
    static getInstance() {
        if (!SwatchDataCache._instance) {
            SwatchDataCache._instance = new SwatchDataCache()
        }
        return SwatchDataCache._instance
    }

    /**
     * Load all swatches and texture swatches in parallel.
     *
     * @param {Object} params
     * @param {Object} params.swatches - Visible swatches (name => id).
     * @param {Object} params.swatchesInvisible - Invisible swatches (name => id).
     * @param {Object} params.textureSwatches - Texture swatches (name => id).
     * @returns {Promise<SwatchDataCache>}
     */
    async loadAll({ swatches = {}, swatchesInvisible = {}, textureSwatches = {} } = {}) {
        if (this.isLoaded) return this
        if (this.loadPromise) return this.loadPromise

        this.isLoading = true
        this.loadPromise = (async () => {
            const visibleList = this._normalizeSwatchIndex(swatches)
            const invisibleList = this._normalizeSwatchIndex(swatchesInvisible)
            const textureList = this._normalizeSwatchIndex(textureSwatches)

            const invisibleIds = new Set(invisibleList.map((item) => item.id))
            const swatchIds = Array.from(new Set([...visibleList, ...invisibleList].map((item) => item.id)))
            const textureIds = textureList.map((item) => item.id)

            this.visibleSwatchIds = new Set(visibleList.map((item) => item.id))
            this.invisibleSwatchIds = new Set(invisibleIds)

            const swatchResults = await Promise.allSettled(swatchIds.map((id) => this._fetchSwatch(id, invisibleIds.has(id))))

            swatchResults.forEach((result, index) => {
                const swatchId = swatchIds[index]
                if (result.status === 'fulfilled') return
                console.warn('[SwatchDataCache] Failed to load swatch', swatchId, result.reason)
            })

            const textureResults = await Promise.allSettled(textureIds.map((id) => this._fetchTextureSwatch(id)))

            textureResults.forEach((result, index) => {
                const textureId = textureIds[index]
                if (result.status === 'fulfilled') return
                console.warn('[SwatchDataCache] Failed to load texture swatch', textureId, result.reason)
            })

            this._buildSearchIndexes()

            this.isLoaded = true
            this.isLoading = false
            return this
        })()

        return this.loadPromise
    }

    /**
     * Return a cached swatch dataset by id (if loaded).
     * @param {number|string} swatchId
     * @returns {Object|null}
     */
    getSwatch(swatchId) {
        return this.allSwatches.get(Number(swatchId)) || null
    }

    /**
     * Return a cached texture swatch dataset by id (if loaded).
     * @param {number|string} textureSwatchId
     * @returns {Object|null}
     */
    getTextureSwatch(textureSwatchId) {
        return this.textureSwatches.get(Number(textureSwatchId)) || null
    }

    /**
     * Return a swatch dataset from cache or fetch it once.
     * @param {number|string} swatchId
     * @returns {Promise<Object>}
     */
    async getSwatchOrFetch(swatchId) {
        return this._fetchSwatch(swatchId, this.invisibleSwatchIds.has(Number(swatchId)))
    }

    /**
     * Return a texture swatch dataset from cache or fetch it once.
     * @param {number|string} textureSwatchId
     * @returns {Promise<Object>}
     */
    async getTextureSwatchOrFetch(textureSwatchId) {
        return this._fetchTextureSwatch(textureSwatchId)
    }

    /**
     * Get visible swatch metadata list.
     * @returns {Array<{id:number,name:string}>}
     */
    getVisibleSwatches() {
        return Array.from(this.visibleSwatches.values()).sort((a, b) => a.name.localeCompare(b.name))
    }

    /**
     * Get invisible swatch metadata list.
     * @returns {Array<{id:number,name:string}>}
     */
    getInvisibleSwatches() {
        return Array.from(this.invisibleSwatches.values()).sort((a, b) => a.name.localeCompare(b.name))
    }

    /**
     * Get compute entries for a swatch.
     * @param {number} swatchId
     * @returns {Array<Object>}
     */
    getComputeColorsBySwatch(swatchId) {
        return this.computeColorsBySwatch.get(Number(swatchId)) || []
    }

    /**
     * Compute closest colors by deltaE from a compute entry pool.
     * @param {{l:number,a:number,b:number}} baseLab
     * @param {Array<{id:number,lab:{l:number,a:number,b:number},color:Object,swatchName?:string,swatchId?:number}>} pool
     * @param {{limit?:number,avoidId?:number|null}} [options]
     * @returns {Array<{deltaE:number,swatchColor:Object,swatchName?:string,swatchId?:number}>}
     */
    findClosestByDeltaE(baseLab, pool, { limit = 10, avoidId = null } = {}) {
        const best = []
        const max = Number.isFinite(limit) ? Math.max(0, limit) : 10
        if (!max || !Array.isArray(pool)) return best

        pool.forEach((entry) => {
            if (!entry) return
            if (avoidId && Number(entry.id) === Number(avoidId)) return
            const deltaE = ColorsMath.cxDeltaECIE2000Default(baseLab, entry.lab)
            if (!Number.isFinite(deltaE)) return
            const row = {
                deltaE,
                swatchColor: entry.color,
                swatchName: entry.swatchName,
                swatchId: entry.swatchId,
            }

            if (best.length < max) {
                best.push(row)
                best.sort((a, b) => a.deltaE - b.deltaE)
                return
            }

            if (deltaE >= best[best.length - 1].deltaE) return
            best.pop()
            best.push(row)
            best.sort((a, b) => a.deltaE - b.deltaE)
        })

        return best
    }

    /**
     * Search across all swatch colors (global).
     * @param {string} query
     * @returns {Array<Object>}
     */
    getColorSearchResults(query) {
        const q = Utils.normalizeText(query)
        if (!q) return []
        return this.colorSearchIndex.filter((row) => row.searchText.includes(q))
    }

    /**
     * Search across textures (swatch colors with image filenames).
     * @param {string} query
     * @returns {Array<Object>}
     */
    getTextureColorSearchResults(query) {
        const q = Utils.normalizeText(query)
        if (!q) return []
        return this.colorSearchIndex.filter((row) => row.color.color_filename && row.searchText.includes(q))
    }

    /**
     * Search across all texture swatch textures (global).
     * @param {string} query
     * @returns {Array<Object>}
     */
    getTextureSwatchSearchResults(query) {
        const q = Utils.normalizeText(query)
        if (!q) return []
        return this.textureSearchIndex.filter((row) => row.searchText.includes(q))
    }

    /**
     * Legacy alias for texture-swatch search.
     * @deprecated Prefer getTextureSwatchSearchResults.
     */
    getTextureSearchResults(query) {
        return this.getTextureSwatchSearchResults(query)
    }

    /**
     * Total count of searchable colors.
     * @returns {number}
     */
    getColorSearchCount() {
        return this.colorSearchIndex.length
    }

    /**
     * Total count of searchable textures (color swatches with image filenames).
     * @returns {number}
     */
    getTextureColorSearchCount() {
        return this.colorSearchIndex.filter((row) => row.color.color_filename).length
    }

    /**
     * Total count of searchable texture swatch entries.
     * @returns {number}
     */
    getTextureSwatchSearchCount() {
        return this.textureSearchIndex.length
    }

    /**
     * Legacy alias for texture-swatch count.
     * @deprecated Prefer getTextureSwatchSearchCount.
     */
    getTextureSearchCount() {
        return this.getTextureSwatchSearchCount()
    }

    /**
     * Get a cached swatch color entry (color + swatch/page context).
     * @param {number|string} swatchColorId
     * @returns {{color:Object, swatchPage:Object, swatchId:number}|null}
     */
    getSwatchColorMetaById(swatchColorId) {
        return this.swatchColorById.get(Number(swatchColorId)) || null
    }

    /**
     * Normalize map-like data (name => id) into an array of objects.
     * @param {Object} map
     * @returns {Array<{id:number,name:string}>}
     */
    _normalizeSwatchIndex(map) {
        return Object.entries(map || {}).map(([name, id]) => ({
            id: Number(id),
            name: String(name || '').trim(),
        }))
    }


    /**
     * Fetch a swatch dataset with in-flight de-duplication.
     * @param {number|string} swatchId
     * @param {boolean} isInvisible
     * @returns {Promise<Object>}
     */
    async _fetchSwatch(swatchId, isInvisible = false) {
        const id = Number(swatchId)
        if (this.allSwatches.has(id)) return this.allSwatches.get(id)
        if (this.pendingSwatchRequests.has(id)) return this.pendingSwatchRequests.get(id)

        const request = SwatchAPI.get_swatch(id)
            .then((data) => {
                this._storeSwatch(data, isInvisible || this.invisibleSwatchIds.has(id))
                return data
            })
            .finally(() => {
                this.pendingSwatchRequests.delete(id)
            })

        this.pendingSwatchRequests.set(id, request)
        return request
    }

    /**
     * Fetch a texture swatch dataset with in-flight de-duplication.
     * @param {number|string} textureSwatchId
     * @returns {Promise<Object>}
     */
    async _fetchTextureSwatch(textureSwatchId) {
        const id = Number(textureSwatchId)
        if (this.textureSwatches.has(id)) return this.textureSwatches.get(id)
        if (this.pendingTextureRequests.has(id)) return this.pendingTextureRequests.get(id)

        const request = SwatchAPI.get_texture_swatch(id)
            .then((data) => {
                this._storeTextureSwatch(data)
                return data
            })
            .finally(() => {
                this.pendingTextureRequests.delete(id)
            })

        this.pendingTextureRequests.set(id, request)
        return request
    }

    /**
     * Store a swatch dataset and prepare compute/search caches.
     * @param {Object} swatch
     * @param {boolean} isInvisible
     */
    _storeSwatch(swatch, isInvisible) {
        if (!swatch || !swatch.id) return

        const swatchId = Number(swatch.id)
        const swatchName = swatch.name || `Swatch ${swatchId}`
        const swatchMeta = { id: swatchId, name: swatchName }

        if (this.allSwatches.has(swatchId)) {
            if (isInvisible) {
                this.invisibleSwatches.set(swatchId, swatchMeta)
                this.invisibleSwatchIds.add(swatchId)
                this.visibleSwatches.delete(swatchId)
                this.visibleSwatchIds.delete(swatchId)
            } else {
                this.visibleSwatches.set(swatchId, swatchMeta)
                this.visibleSwatchIds.add(swatchId)
                this.invisibleSwatches.delete(swatchId)
                this.invisibleSwatchIds.delete(swatchId)
            }
            return
        }

        this.allSwatches.set(swatchId, swatch)
        if (isInvisible) {
            this.invisibleSwatches.set(swatchId, swatchMeta)
            this.invisibleSwatchIds.add(swatchId)
        } else {
            this.visibleSwatches.set(swatchId, swatchMeta)
            this.visibleSwatchIds.add(swatchId)
        }

        const colors = []
        const computeRows = []

        ;(swatch.swatch_pages || []).forEach((page) => {
            ;(page.swatch_colors || []).forEach((color) => {
                if (!color || !color.id) return

                const entry = {
                    ...color,
                    swatch: color.swatch || { id: swatchId, name: swatchName },
                    swatch_id: color.swatch_id ?? swatchId,
                }

                colors.push(entry)
                this.swatchColorById.set(Number(entry.id), {
                    color: entry,
                    swatchPage: page,
                    swatchId,
                })

                const lab = {
                    l: Number(entry.labl),
                    a: Number(entry.laba),
                    b: Number(entry.labb),
                }

                if (Number.isFinite(lab.l) && Number.isFinite(lab.a) && Number.isFinite(lab.b)) {
                    computeRows.push({
                        id: Number(entry.id),
                        swatchId,
                        swatchName,
                        lab,
                        color: entry,
                    })

                    this.computeColorList.push({
                        id: Number(entry.id),
                        swatchId,
                        l: lab.l,
                        a: lab.a,
                        b: lab.b,
                    })
                }
            })
        })

        this.swatchColorsBySwatch.set(swatchId, colors)
        this.computeColorsBySwatch.set(swatchId, computeRows)
    }

    /**
     * Store a texture swatch dataset and build a texture index.
     * @param {Object} textureSwatch
     */
    _storeTextureSwatch(textureSwatch) {
        if (!textureSwatch || !textureSwatch.id) return

        const textureSwatchId = Number(textureSwatch.id)
        const textureSwatchName = textureSwatch.name || `Texture ${textureSwatchId}`

        if (this.textureSwatches.has(textureSwatchId)) return

        this.textureSwatches.set(textureSwatchId, textureSwatch)
        ;(textureSwatch.texture_pages || []).forEach((page) => {
            ;(page.textures || []).forEach((texture) => {
                if (!texture || !texture.id) return
                this.texturesById.set(Number(texture.id), {
                    texture,
                    textureSwatchId,
                    textureSwatchName,
                    texturePage: page,
                })
            })
        })
    }

    /**
     * Build global search indexes for colors and textures.
     */
    _buildSearchIndexes() {
        this.colorSearchIndex = []
        this.textureSearchIndex = []

        this.swatchColorsBySwatch.forEach((colors, swatchId) => {
            colors.forEach((color) => {
                const searchText = Utils.normalizeText(
                    [color.name, color.name2, color.code, color.color_code, color.reference, color.id].filter(Boolean).join(' '),
                )

                this.colorSearchIndex.push({
                    id: Number(color.id),
                    swatchId,
                    swatchName: color.swatch?.name,
                    color,
                    searchText,
                })
            })
        })

        this.texturesById.forEach((entry) => {
            const texture = entry.texture
            const searchText = Utils.normalizeText(
                [texture.name, texture.code, texture.color_code, texture.reference, texture.id].filter(Boolean).join(' '),
            )

            this.textureSearchIndex.push({
                id: Number(texture.id),
                textureSwatchId: entry.textureSwatchId,
                textureSwatchName: entry.textureSwatchName,
                texture,
                searchText,
            })
        })
    }

}

SwatchDataCache._instance = null

export default SwatchDataCache
